import { clearAppStorage } from 'src/common/storage';
import axios from 'axios';
import { useGlobalState } from 'src/state/globalstate';
import { captureApiFailure, captureLogout, captureError } from 'src/common/analytics';
import {
  classifyConnection,
  reportRequestFailure,
  reportRequestStalled,
  reportRequestSuccess,
  reportServiceUnavailable,
  setProbeUrl
} from 'src/common/connection';
import {
  logEvent,
  logInfo,
  logWarn,
  noteOutageFailure
} from 'src/common/frontendLog';

// baseURL: 'https://regtechapi.kymerasolutions.com/api', //Kymera Solution production
// baseURL: 'https://regtechstagapi.kymerasolutions.com/api', //Kymera Solution staging
// baseURL: 'https://api.mlrcsolution.com/api', // ark UI2 production new2 (09-feb-2026)
export const API_BASE_URL = 'https://mlrcapi.kymerasolutions.com/api'; // Kymera 2

// ── Request timeouts ─────────────────────────────────────────────────────────
// Axios defaults to NO timeout, which is what left the UI stuck: a request sent
// over a socket that died while the machine was idle never errors and never
// resolves, so the caller's `.then` never runs, the spinner spins forever and
// the button stays disabled until someone hard-refreshes (which drops the dead
// socket pool — hence "a refresh fixes it"). A timeout guarantees every request
// eventually settles, so the callers' existing catch/finally paths can run.
//
// The default is deliberately generous — the connection bar reacts within
// SLOW_REQUEST_MS, so a long timeout no longer means a silent UI.
const DEFAULT_TIMEOUT_MS = 90000;
/**
 * Endpoints that are legitimately slow — AI passes, search over large corpora,
 * and the ECS power-on calls, which routinely take tens of seconds. Matched as
 * substrings of the request url.
 */
const LONG_RUNNING = [
  'statusService',
  'createService',
  'fileProcess',
  'imageProcess',
  'realTimeTextProcess',
  'realTimeImageSearch',
  'Bedrock',
  'smartSearch',
  'elasticsearch',
  'advanceSearchFiles',
  'triggerClaimsModel',
  'USletterQuery',
  'readS3Json',
  'claimDict'
];
const LONG_TIMEOUT_MS = 600000;
const isLongRunning = (url?: string) =>
  !!url && LONG_RUNNING.some((fragment) => url.includes(fragment));

/**
 * How long a single request may stay unsettled before we go and check whether
 * the connection is still there. Only a probe can tell "slow endpoint" from
 * "dead socket", so this is a question, not a verdict — see common/connection.
 */
const SLOW_REQUEST_MS = 12000;

//URL fo API request
export const Axios = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,

  headers: {
    'Content-type': 'application/json'
  }
});

// Aim the reachability probe at our own API host: reaching some other origin
// would prove nothing about the one the app depends on.
setProbeUrl(`${API_BASE_URL}/getVersion`);

//Project Name
export const projectName = {
  Path: 'Kymera Solutions' //Kymera Solution
};

export const S3bucket = {};

export const Segment = {
  tracking: false //true or false
};

//Check for file statuses
export const AxiosNetwork = axios.create({
  baseURL: 'https://inabia.com/inabia-ai-crobjob',
  // Same reasoning as the main instance: never leave a caller hanging on a
  // socket that will not answer.
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-type': 'application/json'
  }
});

// ── Stall watchdog ───────────────────────────────────────────────────────────
// Arms a timer per request; whichever way the request settles, the timer is
// cancelled. A timer that actually fires means the request has been hanging,
// which asks common/connection to go and check the wire. Losing the id (axios
// can drop unknown config keys when it re-merges a config for a retry) costs
// nothing worse than one extra probe, so this is safe to be approximate.
let stallSeq = 0;
/**
 * Per-in-flight-request record. The timer is the original stall watchdog; the
 * rest is what the activity log needs to describe the request afterwards —
 * WHICH endpoint, and HOW LONG the user actually waited on it. Held here rather
 * than on `config` because axios can drop unknown config keys when it re-merges
 * for a retry.
 */
const inFlight = new Map<
  number,
  { timer: ReturnType<typeof setTimeout>; url?: string; method?: string; startedAt: number }
>();

const armStallWatch = (config: any) => {
  const id = ++stallSeq;
  const url = config?.url;
  const method = config?.method;
  const startedAt = Date.now();
  const timer = setTimeout(() => {
    const record = inFlight.get(id);
    inFlight.delete(id);
    // The request is still unsettled after SLOW_REQUEST_MS — this is the exact
    // moment the user is staring at a spinner with nothing to show for it, and
    // the entry we had no way to record before. It is a question, not a verdict:
    // a legitimately slow endpoint logs this too, which is why the probe result
    // (connection_lost / nothing) is what tells them apart.
    logWarn('api_stalled', {
      msg: `Request still unsettled after ${SLOW_REQUEST_MS}ms — user is on a spinner`,
      reason: 'stalled',
      reqId: `r_${id}`,
      url,
      method,
      durationMs: record ? Date.now() - record.startedAt : SLOW_REQUEST_MS,
      extra: { timeoutMs: config?.timeout, longRunningEndpoint: isLongRunning(url) }
    });
    reportRequestStalled();
  }, SLOW_REQUEST_MS);
  inFlight.set(id, { timer, url, method, startedAt });
  config.__stallId = id;
};

/**
 * Stop the watchdog and report how long the request took. Returns the elapsed ms
 * when the record is still around, so failures can be logged with a real
 * duration; a lost id (see above) just costs us that one number.
 */
const disarmStallWatch = (config: any): { reqId?: string; durationMs?: number } => {
  const id = config?.__stallId;
  if (!id) return {};
  const record = inFlight.get(id);
  if (record) {
    clearTimeout(record.timer);
    inFlight.delete(id);
    return { reqId: `r_${id}`, durationMs: Date.now() - record.startedAt };
  }
  return { reqId: `r_${id}` };
};

//Initialize user login parameters for API calls
Axios.interceptors.request.use((config) => {
  armStallWatch(config);
  // Give the genuinely slow endpoints room; everything else keeps the default,
  // and an explicit per-call timeout still wins.
  if (config.timeout === DEFAULT_TIMEOUT_MS && isLongRunning(config.url)) {
    config.timeout = LONG_TIMEOUT_MS;
  }

  const userId = localStorage.getItem('userId');
  let langId = '2';
  let langType = localStorage.getItem('languageType');
  // Guard against the literal strings "null"/"undefined" that get written when
  // session-restore does setItem('languageType', getItem(...)) on a missing
  // value — those are truthy and would send langId=null. Default to '2' (EN).
  if (langType && langType !== 'null' && langType !== 'undefined') {
    langId = langType;
  }

  const token = localStorage.getItem('token');
  const times = Date.now();
  if (userId && token) {
    config.params = {
      userId,
      token,
      times,
      langId,
      ...config.params
    };
  } else if (!userId && !token) {
    const token = 'sdddwddwdwdwwd';
    config.params = {
      times,
      langId,
      ...config.params
    };
  }
  return config;
});

// Retry server errors above 500 (501 / 502 / 503): 3 attempts, 30s apart —
// those mean the backend is coming up, and it needs the time.
const RETRY_MAX = 3;
const RETRY_DELAY_MS = 30000;
const RETRY_STATUSES = [501, 502, 503];
/**
 * Transport failures get a much shorter ladder instead. The common cause is a
 * socket that went stale while the tab sat idle, and that recovers on the very
 * next attempt — waiting 30s to find that out (three times over) is 90 seconds
 * of the user staring at a spinner for something a second would have fixed.
 */
const NETWORK_RETRY_DELAYS_MS = [1000, 3000, 8000];
const retryDelay = (ms: number) =>
  // Jitter so a page that fired ten requests at once doesn't retry them all on
  // the same tick and stampede a recovering connection.
  new Promise((resolve) => setTimeout(resolve, ms + Math.random() * 400));

/**
 * A failure with no HTTP response at all: DNS/socket error, timeout, or a
 * CORS-stripped gateway error. Explicitly NOT a cancellation, which is the app
 * abandoning its own request and says nothing about the network.
 */
const isTransportFailure = (error: any) =>
  !error?.response &&
  !axios.isCancel(error) &&
  error?.code !== 'ERR_CANCELED';

// ── Session timeout / unauthorized handling ──────────────────────────────────
// The backend signals an expired session either as HTTP 401 or as a 200 whose
// body is the string "Unauthorized". Handle it CENTRALLY here so the Unauthorized
// response never reaches a caller's `.then` (where consuming it as data would
// throw and trip the app's error boundary → "Something went wrong"). Instead we
// clear the session and land on a clean /login screen with a session toast.
let sessionExpiredHandled = false;
const handleSessionExpired = (source?: { url?: string; status?: number }) => {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  // Record the logout BEFORE wiping storage and hard-redirecting below.
  // captureLogout sends instantly (sendBeacon), so it survives the navigation.
  captureLogout('session_expired');
  // The activity-log entry is written synchronously to localStorage, so it
  // survives both the clearAppStorage() below (the `__felog` prefix is preserved)
  // and the full-page redirect, and ships on the next load. Without it, a user
  // bounced to /login mid-task left no record of which request ended the session.
  logWarn('session_expired', {
    msg: 'Session rejected by the API — clearing storage and redirecting to /login',
    reason: 'unauthorized',
    url: source?.url,
    status: source?.status ?? null
  });
  try {
    // Preserve the chosen language across the wipe.
    const language = localStorage.getItem('language');
    const languageType = localStorage.getItem('languageType');
    clearAppStorage();
    if (language) localStorage.setItem('language', language);
    if (languageType) localStorage.setItem('languageType', languageType);
    // Flag read by the login page to show the "session expired" toast (survives
    // the full reload below, unlike in-memory global state).
    sessionStorage.setItem('sessionExpired', '1');
  } catch (e) { captureError('common/api:100', e); }
  // Full redirect resets any error-boundary/page state from the failed request
  // so the user lands on a clean login screen. Avoid a redirect loop if we're
  // already on /login.
  if (!/\/login\/?$/.test(window.location.pathname)) {
    window.location.replace('/login');
  }
};

Axios.interceptors.response.use(
  (response) => {
    const timing = disarmStallWatch(response?.config);
    // A request that only succeeded because it was retried is the other half of
    // the outage story: it says the services came back, and when. Logged only in
    // that case — logging every successful call would drown the file in noise.
    const attempts = Number((response?.config?.params as any)?.__retry) || 0;
    if (attempts > 0) {
      logInfo('api_recovered', {
        msg: `Request succeeded on attempt ${attempts + 1} — services are answering again`,
        reason: 'retry_succeeded',
        url: response?.config?.url,
        method: response?.config?.method,
        status: response?.status,
        attempt: attempts + 1,
        ...timing
      });
    }
    // Anything that came back proves the connection works, so a bar raised by
    // an earlier failure clears itself the moment traffic flows again — the
    // user doesn't have to guess whether it's safe to carry on.
    reportRequestSuccess();
    // 200 with body === "Unauthorized" → expired session.
    if (response && response.data === 'Unauthorized') {
      handleSessionExpired({ url: response?.config?.url, status: response?.status });
      // Never resolve: halt the caller so it can't consume "Unauthorized" as
      // data and throw. The page is redirecting away regardless.
      return new Promise(() => {});
    }
    return response;
  },
  async (error) => {
    const config = error?.config;
    const status = error?.response?.status;
    const timing = disarmStallWatch(config);

    const transportFailure = isTransportFailure(error);
    if (transportFailure) {
      reportRequestFailure();
      // Attribute this request to the open outage window (if any), so the
      // summary written when the connection returns can say how many calls the
      // user lost — the "how bad was it" number.
      noteOutageFailure();
    } else if (RETRY_STATUSES.includes(status)) {
      // A readable gateway status from the set we already treat as "the backend is
      // coming up" is direct proof: the host is answering, the services are not.
      // This used to call reportRequestSuccess (the wire is fine, after all),
      // which cleared the bar on the very failure that should raise it and left
      // the user with no notice at all until the error page appeared.
      //
      // Deliberately NOT every 5xx: a plain 500 is an application fault, and
      // reporting it as an outage would both mislabel it and — via the
      // ErrorFoundGlobal guard in state/globalstate — suppress the error page that
      // is the correct response to a real bug.
      reportServiceUnavailable();
      noteOutageFailure();
    } else if (error?.response) {
      // The server answered with something under 500 — it is working and saying
      // no, which is not an outage.
      reportRequestSuccess();
    }

    // Expired session as HTTP 401 → same central handling (never reject into the
    // caller's catch, which may surface the error page).
    if (status === 401 || error?.response?.data === 'Unauthorized') {
      handleSessionExpired({ url: config?.url, status });
      return new Promise(() => {});
    }

    // Only url/method/status ever leave the app — never config.params, which
    // carries the auth token and userId.
    const failureMeta = {
      url: config?.url,
      method: config?.method,
      status,
      code: error?.code
    };

    /**
     * Both sinks get every failure: PostHog for aggregate dashboards, the
     * activity log for the per-user, per-day file you can read back after the
     * fact. `attempts` and `durationMs` are what make an entry diagnostic rather
     * than decorative — they say how long the user waited before the app gave up.
     */
    const logFailure = (
      reason: string,
      msg: string,
      level: 'warn' | 'error',
      counts?: {
        /** Retries performed — kept as-is so existing PostHog dashboards don't shift. */
        retries?: number;
        /** Total attempts INCLUDING the first, which is what reads naturally in a log. */
        attempts?: number;
      }
    ) => {
      captureApiFailure({
        ...failureMeta,
        ...(counts?.retries !== undefined ? { retries: counts.retries } : {}),
        reason: reason as any
      });
      logEvent(level, 'api_failed', {
        msg,
        reason,
        ...failureMeta,
        status: status ?? null,
        attempt: counts?.attempts,
        ...timing,
        extra: {
          axiosMessage: error?.message,
          canceled: axios.isCancel(error),
          timeoutMs: config?.timeout
        }
      });
    };

    // Do not retry for client errors (e.g., 400–499) — these are caller-side
    // problems (bad request, auth, not found) that won't succeed on a retry.
    if (status && status >= 400 && status < 500) {
      // A 4xx is the server working correctly and saying no, so it is a warning,
      // not an outage. Keeping it below `error` also keeps it from triggering the
      // immediate flush reserved for the failures that matter.
      logFailure(
        'client_error',
        `API rejected the request with ${status} — not retried`,
        'warn'
      );
      return Promise.reject(error);
    }

    // A gateway 501/502/503 is frequently CORS-blocked (no
    // Access-Control-Allow-Origin on the error), so it reaches the browser as a
    // network error with NO readable `response` (status === undefined). Treat
    // those as retriable too — otherwise the 5xx falls straight through to the
    // "Something went wrong" page with no retry at all.
    const noResponse = transportFailure;
    const retriable = RETRY_STATUSES.includes(status) || noResponse;

    if (!config || !retriable) {
      logFailure(
        noResponse ? 'no_response' : 'unretriable',
        noResponse
          ? 'Request never reached the server (no response at all) and could not be retried'
          : `Failed with ${status} — not in the retriable set`,
        'error'
      );
      return Promise.reject(error);
    }

    // Track the attempt count in `params` (a known axios key that is always
    // preserved across the re-merge Axios(config) does) — a plain custom
    // property like config.__retryCount can be dropped on re-merge, which made
    // the count reset to 0 every time and retry forever.
    const params = (config.params || {}) as Record<string, any>;
    const retryCount = Number(params.__retry) || 0;
    if (retryCount >= RETRY_MAX) {
      // 3 retries exhausted — drop the marker and let the failure surface
      // (e.g. the "Something went wrong" page).
      delete params.__retry;
      // THE entry for the reported scenario: every attempt was made, none
      // landed, and the very next thing the user sees is the recovery screen.
      // Logged at error level so it flushes to the sink within seconds.
      logFailure(
        'retries_exhausted',
        `Gave up after ${retryCount + 1} attempts — the user is about to see the recovery screen`,
        'error',
        { retries: retryCount, attempts: retryCount + 1 }
      );
      return Promise.reject(error);
    }

    config.params = { ...params, __retry: retryCount + 1 };

    // Seconds for a dropped connection, half a minute for a backend still getting
    // to its feet. Picking between them is the whole problem: a CORS-stripped 503
    // arrives here looking exactly like a dead socket (`noResponse`), so this used
    // to put a service that needs minutes to boot on the 1s/3s/8s ladder and burn
    // all four attempts inside twelve seconds — visibly "3 attempts in 5 seconds"
    // in the network panel, and then the error page.
    //
    // The first retry still goes fast unconditionally: a socket that went stale
    // while the tab was idle recovers on the very next attempt, and that case is
    // common enough to be worth one cheap try before spending any time deciding.
    // From the second retry on, ask the probe what is actually wrong — it can read
    // a status, so it can tell "answering with 5xx" from "not answering", and the
    // answer is deduped and cached, so asking costs nothing per request.
    let delayMs: number;
    if (!noResponse) {
      // Readable 5xx — no ambiguity to resolve.
      delayMs = RETRY_DELAY_MS;
    } else if (retryCount === 0) {
      delayMs = NETWORK_RETRY_DELAYS_MS[0];
    } else {
      const verdict = await classifyConnection();
      delayMs =
        verdict === 'unavailable'
          ? RETRY_DELAY_MS
          : NETWORK_RETRY_DELAYS_MS[
              Math.min(retryCount, NETWORK_RETRY_DELAYS_MS.length - 1)
            ];
    }
    // Log each attempt individually so the file shows the ladder as it was
    // climbed — the timestamps are how you reconstruct the two or three minutes
    // the user spent waiting for the services to come up.
    logWarn('api_retry', {
      msg: `Attempt ${retryCount + 1} failed — retrying in ${delayMs}ms`,
      reason: noResponse ? 'no_response' : `status_${status}`,
      ...failureMeta,
      status: status ?? null,
      attempt: retryCount + 1,
      ...timing,
      extra: { retryInMs: delayMs, maxAttempts: RETRY_MAX + 1 }
    });
    await retryDelay(delayMs);
    return Axios(config);
  }
);

// Reg Tech Api Calls
export enum API {
  AddCase = '/addCase',
  GetCases = '/cases',
  FileUpload = '/fileUpload',
  TagsUpdate = '/tagsUpdate',
  Edit = '/edit',
  Docs = '/docsSQL',
  NetworkError = '/ediscoveryAPI.php',
  forgotPassAWS = '/forgotPassAWS',
  forgotPassCodeAWS = '/forgotPassCodeAWS',
  Files = '/filesSQL',
  eFiles = '/excelDocsNew',
  loginAWS = '/loginAWSSQL',
  registerAWS = '/signupAWSSQL',
  AWSauth = '/AWSauth',
  signupAWSCode = '/signupAWSCode',
  Search = '/advanceSearchFiles',
  CaseSettings = '/case-settings',
  changePassAWS = '/changePassAWS',
  Viewer = '/viewerSQL',
  Addtags = '/tagsUpdate',
  deleteCase = '/deleteCase',
  editCase = '/editCase',
  deleteDocs = '/deleteDocsSQL',
  DocType = 'docsGraphBardocType',
  columnSelection = 'columnSelection',
  VersionCheck = 'updateVersionSQL',
  GetConfig = 'formatList',
  updateUserFormat = 'updateUserFormat',
  fileUploadS3 = 'fileUploadS3MbotSQL',
  // Document Annotator: dedicated upload + listing endpoints.
  fileUploadS3MbotAnnotation = 'fileUploadS3MbotAnnotation',
  DocsAnnotator = '/DocsAnnotator',
  DocsAnnotatorView = '/DocsAnnotatorView',
  updateAnnotatorDocStatus = 'updateAnnotatorDocStatus',
  deleteAnnotatorDoc = 'deleteAnnotatorDoc',
  // Annotation labels: list (GET), create/update (POST), delete (POST).
  DocsAnnotatorLabels = '/DocsAnnotatorLabels',
  DocsAnnotatorLabel = '/DocsAnnotatorLabel',
  deleteAnnotatorLabel = '/deleteAnnotatorLabel',
  S3UploadPath = 'getS3BucketName',
  ResendVerificationCode = 'resendConfirmationCodeAWS',
  statusService = 'statusService',
  createService = 'createService',
  melasticsearch = 'elasticsearch',
  tagsMlrc = 'tagsSQL',
  postTags = 'postTagsSQL',
  docTypeMlrc = 'docTypeSQL',
  postType = 'postTypeSQL',
  fileProcess = 'fileProcessSQL',
  postDeleteTags = 'postDeleteTagsSQL',
  statusServiceStaging = 'statusServiceStaging',
  createServiceStaging = 'createServiceStaging',
  getCommercials = 'getCommercials',
  updateStatusStopped = 'updateStatusStopped',
  fileUploadS3RealTime = 'fileUploadS3RealTimeSQL',
  docsRealTime = 'docsRealTimeSQL',
  addThumbsUp = 'addThumbsUp',
  viewThumbsUp = 'viewThumbsUpSQL',
  addThumbsUpNew = 'addThumbsUpLatest',
  updateThumbsUp = 'updateThumbsUpSQL',
  userDetails = 'userDetailsSQL',
  rules = 'rulesStaging',
  viewThumbsUpDownload = 'viewThumbsUpDownloadSQL',
  addThumbsUpLatestSQL = 'addThumbsUpLatestSQL',
  deleteThumbsUp = 'deleteThumbsUp',
  viewThumbsUpUsersSQL = 'viewThumbsUpUsersSQL',
  PDFListing = 'PIIviewerDocList',
  mlrcBedrock = 'Bedrock',
  userPreferences = 'userPreferences',
  realTimeTextProcess = 'realTimeTextProcess',
  imageUploadS3MbotSQL = 'imageUploadS3MbotSQL',
  imageMlrcSQL = 'imageSQL',
  imageProcessSQL = 'imageProcessSQL',
  getUserPref = 'getUserPref',
  deleteImgSQL = 'deleteImgSQL',
  updateStatusImgStopped = 'updateStatusImgStopped',
  addRulesStaging = 'addRulesStaging',
  updateRulesStaging = 'updateRulesStaging',
  viewThumbDelete = 'viewThumbDelete',
  readS3Json = 'readS3Json',
  realTimeImageSearch = 'realTimeImageSearch',
  statusServiceImage = 'statusServiceImage',
  adminIds = 'adminIds',
  aaGuidelines = 'aaGuidelines',
  getLoginData = 'getLoginData',
  LogAccessEmails = 'LogAccessEmails',
  getVersion = 'getVersion',
  getFeatures = 'getFeatures',
  searchType = 'searchType',
  rulesSemantic = 'rulesSemantic',
  claimFormOptions = 'claimFormOptions',
  userManagementListing = 'userManagementListing',
  userRoles = 'userRoles',
  roleLimits = 'roleLimits',
  demoSignup = 'demoSignup',
  demoLogin = 'demoLogin',
  updateRole = 'updateRole',
  deleteRules = 'deleteRules',
  checkRules = 'checkRules',
  addProductTag = 'addProductTag',
  triggerClaimsModel = 'triggerClaimsModel',
  getGoogleAccessToken = 'get-signed-url',
  // --- Synced from Ark UI2 ---
  smartSearch = 'smartSearch',
  smartSearchUpdateId = 'smartSearchUpdateId',
  smartSerachAccessEmails = 'smartSerachAccessEmails',
  smartSearchHistory = 'smartSearchHistory',
  OtherTagsArray = 'OtherTagsArray',
  tagActions = 'tagActions',
  isiSelectors = 'isiSelectors',
  USletterQuery = 'USletterQuery',
  USletterStatus = 'USletterStatus',
  getUSLetters = 'getUSLetters',
  getFDALetterDetail = 'getFDALetterDetail',
  libraryFileSelect = 'libraryFileSelect',
  tacticSummaryDetails = 'tacticSummaryDetails',
  statusServiceUS = 'statusServiceUS',
  createServiceUS = 'createServiceUS',
  Irs = 'Irs',
  getIRSOptions = 'getIRSOptions',
  getCases = 'getCases',
  getCaseDetails = 'getCaseDetails',
  getBreachDetails = 'getBreachDetails',
  updateCitationStatus = 'updateCitationStatus',
  fileUploadS3RealTimeSQLNew = 'fileUploadS3RealTimeSQLNew',
  activityLogs = 'activityLogs',
  claimDict = 'claimDict'
}

export default Axios;
